<template>
    <CostableTableRow
        class="CostableTableCost position-relative"
        :class="{ 'table-success': cost.reconciled, 'table-info': cost.is_approved }"
    >
        <template v-slot:drag>
            <span
                v-if="$store.getters['costable/isEditable']"
                v-b-tooltip.hover.right.d50
                class="CostableTableCostHandle"
                title="Drag to Reorder"
            >
                <i class="fa fa-bars" />
            </span>
        </template>

        <template v-slot:select>
            <CostableTableSelectCheckbox
                v-if="$store.getters['costable/isEditable']"
                :value="cost.selected"
                :disabled="!isEditable"
                @input="selectCost"
            />
        </template>

        <template v-slot:taxRate>
            <EditableTaxRateSelect
                v-if="isEditable && !cost.is_approved"
                tooltip="Click to change tax rate..."
                :value="cost.taxRate"
                @save="patchCost($event,'tax_rate_id')"
            >
                <div v-if="cost.effective_tax_rate">
                    {{ cost.effective_tax_rate }}
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableTaxRateSelect>
            <div v-else>
                {{ cost.effective_tax_rate }}
            </div>
        </template>

        <template v-slot:tab>
            <EditableTabSelect
                v-if="isEditable && !cost.group_id && !cost.is_approved"
                tooltip="Click to change tab..."
                :value="cost.tab"
                @save="patchCost($event, 'tab_id')"
            >
                <div v-if="cost.tab">
                    {{ cost.tab.name }}
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableTabSelect>
            <div v-else>
                <div v-if="cost.tab">
                    {{ cost.tab.name }}
                </div>
            </div>
        </template>

        <template v-slot:type>
            <EditableTypeSelect
                v-if="isEditable && !cost.is_approved"
                tooltip="Click to change type..."
                :value="cost.type"
                @save="patchCost($event, 'cost_type_id')"
            >
                <div v-if="cost.type">
                    {{ cost.type.label }}
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableTypeSelect>
            <div v-else>
                <div v-if="cost.type">
                    {{ cost.type.label }}
                </div>
            </div>
        </template>
        <template v-slot:nominalCode>
            <EditableNominalCodeSelect
                v-if="isEditable && !cost.is_approved && showNominalCodeColumn"
                tooltip="Click to change code..."
                :value="cost.nominal_code"
                @save="patchCost($event, 'nominal_code_id')"
            >
                <div v-if="cost.nominal_code">
                    {{ cost.nominal_code.name }}
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableNominalCodeSelect>
            <div v-else>
                <div v-if="cost.nominal_code && cost.enabled_nominal_codes">
                    {{ cost.nominal_code.name }}
                </div>
            </div>
        </template>

        <template v-slot:description>
            <EditableTextarea
                v-if="isEditable && !cost.is_approved"
                tooltip="Click to add a description..."
                :value="cost.description"
                :markdown="true"
                @save="patchCost($event, 'description')"
            >
                <div
                    v-if="cost.description_html"
                    class="last:mb-0"
                    v-html="cost.description_html"
                />
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableTextarea>
            <div v-else>
                <div
                    v-if="cost.description_html"
                    v-html="cost.description_html"
                />
            </div>
        </template>

        <template v-slot:supplier>
            <EditableAccountSelect
                v-if="isEditable && !cost.is_approved"
                tooltip="Click to change supplier..."
                :value="cost.supplier"
                @save="patchCost($event, 'supplier_account_id')"
            >
                <div v-if="cost.supplier">
                    {{ cost.supplier.name }}
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableAccountSelect>
            <div v-else>
                <div v-if="cost.supplier">
                    {{ cost.supplier.name }}
                </div>
            </div>
        </template>

        <template v-slot:quantity>
            <EditableInput
                v-if="isEditable && !cost.is_approved"
                class="d-inline-block"
                format
                tooltip="Click to edit quantity value..."
                :value="formatFloat(cost.original_quantity)"
                @save="patchCost($event, 'original_quantity')"
            >
                {{ formatFloat(cost.original_quantity) }}
            </EditableInput>
            <div v-else>
                {{ formatFloat(cost.original_quantity) }}
            </div>
            <div
                v-if="cost.quantity.unit"
                class="d-inline-block"
            >
                <EditableInput
                    v-if="isEditable && !cost.is_approved"
                    tooltip="Click to edit quantity unit..."
                    :value="cost.quantity.unit"
                    @save="patchCost($event, 'quantity_unit')"
                >
                    {{ cost.quantity.unit }}
                </EditableInput>
                <span v-else>
                    {{ cost.quantity.unit }}
                </span>
            </div>
        </template>

        <template v-slot:duration>
            <span
                v-if="cost.duration && cost.duration.enabled && cost.duration.time.enabled"
                class="no-wrap"
            >
                <EditableInput
                    v-if="isEditable && !cost.is_approved"
                    class="d-inline-block"
                    type="time"
                    tooltip="Click to edit duration start time..."
                    :value="cost.duration.time.start || ''"
                    @save="patchCost($event, 'start_time')"
                >
                    {{ cost.duration.time.start ? formatTime(cost.duration.time.start) : '--:--' }}
                </EditableInput>
                <span v-else>
                    {{ cost.duration.time.start ? formatTime(cost.duration.time.start) : '--:--' }}
                </span>
                <span> - </span>
                <EditableInput
                    v-if="isEditable && !cost.is_approved"
                    class="d-inline-block"
                    type="time"
                    tooltip="Click to edit duration end time..."
                    :value="cost.duration.time.end || ''"
                    @save="patchCost($event, 'end_time')"
                >
                    {{ cost.duration.time.end ? formatTime(cost.duration.time.end) : '--:--' }}
                </EditableInput>
                <span v-else>
                    {{ cost.duration.time.end ? formatTime(cost.duration.time.end) : '--:--' }}
                </span>
            </span>
            <span
                v-else
                class="no-wrap"
            >
                <EditableInput
                    v-if="isEditable && !cost.is_approved"
                    class="d-inline-block"
                    format
                    tooltip="Click to edit duration..."
                    :value="cost.duration.quantity.value"
                    @save="patchDuration($event, 'duration')"
                >
                    {{ cost.duration.quantity.value }}
                </EditableInput>
                <span v-else>
                    {{ cost.duration.quantity.value }}
                </span>
                <div v-if="cost.duration.enabled && cost.duration.quantity.unit">
                    <EditableInput
                        v-if="isEditable && !cost.is_approved"
                        class="d-inline-block"
                        tooltip="Click to edit duration unit..."
                        :value="cost.duration.quantity.unit"
                        @save="patchCost($event, 'duration_unit')"
                    >
                        {{ cost.duration.quantity.unit }}
                    </EditableInput>
                    <span v-else>
                        {{ cost.duration.quantity.unit }}
                    </span>
                </div>
            </span>
        </template>

        <template v-slot:unitCost>
            <EditableInput
                v-if="isEditable && !cost.is_approved"
                format
                tooltip="Click to edit unit cost..."
                help="You can also enter a percentage such as '30%' to set the margin, or a mathematical expression such as '1200 / 12'."
                :value="formatFloat(cost.unit_cost_local.value)"
                @save="patchCost($event, 'unit_cost')"
            >
                {{ formatCurrency(cost.unit_cost.value) }}
            </EditableInput>
            <div v-else>
                {{ formatCurrency(cost.unit_cost.value) }}
            </div>
        </template>
        <template v-slot:currencyCost>
            <div v-if="cost.unit_price_local.currency">
                {{ cost.unit_cost_local.currency_formatted }}
            </div>
            <div v-else>
                {{ formatCurrency(cost.unit_cost_local.value) }}
            </div>
        </template>

        <template
            v-slot:unitPrice
            class="font-bold"
        >
            <EditableInput
                v-if="isEditable && !cost.is_approved"
                class="d-inline-block"
                format
                tooltip="Click to edit unit price..."
                help="You can also enter a percentage such as '30%' to set the markup, or a mathematical expression such as '1200 / 12'."
                :value="formatFloat(cost.unit_price_local.value)"
                @save="patchCost($event, 'unit_price')"
            >
                <div class="underline-dotted">
                    {{ formatCurrency(cost.unit_price.value) }}
                </div>
            </EditableInput>
            <span v-else>
                <div class="underline-dotted">
                    {{ formatCurrency(cost.unit_price.value) }}
                </div>
            </span>
            <div v-if="isPriceUnitEnabled">
                <EditableInput
                    v-if="isEditable && !cost.is_approved"
                    class="d-inline-block"
                    tooltip="Click to edit unit price unit..."
                    :value="cost.unit_price.unit"
                    @save="patchCost($event, 'price_unit')"
                >
                    {{ cost.unit_price.unit }}
                </EditableInput>
                <span v-else>
                    {{ cost.unit_price.unit }}
                </span>
            </div>

        </template>

        <template v-slot:currencyPrice>
            <div v-if="cost.unit_price_local.currency">
                {{ cost.unit_price_local.currency_formatted }}
            </div>
            <div v-else>
                {{ formatCurrency(cost.unit_price_local.value) }}
            </div>
        </template>

        <template v-slot:totalCost>
            <span
                v-b-tooltip.hover.top.d50
                title="Total Cost"
            >
                {{ formatCurrency(cost.total_cost) }}
            </span>
        </template>

        <template v-slot:totalPrice>
            <span
                v-b-tooltip.hover.top.d50
                title="Total Price"
            >
                {{ formatCurrency(cost.total_price) }}
            </span>
        </template>
        <template v-slot:markup>
            <span
                v-b-tooltip.hover.top.d50
                :data-html="true"
                :title="'Markup: \n ' + formatPercentage(cost.markup_percentage)"
            >
                {{ formatCurrency(cost.markup) }}
            </span>
        </template>
        <template v-slot:margin>
            <span
                v-b-tooltip.hover.top.d50.tooltip-inner
                :data-html="true"
                :title="'Margin: \n ' + formatCurrency(cost.margin_amount)"
            >
                {{ formatPercentage(cost.margin) }}
            </span>
        </template>
        <template v-slot:discount>
            <span
                v-b-tooltip.hover.top.d50
                title="Discount"
            >
                {{ formatCurrency(cost.discount_amount) }}
            </span>
        </template>
        <template v-slot:fee>
            <span
                v-b-tooltip.hover.top.d50
                title="Management fee"
            >
                {{ formatCurrency(cost.management_fee_amount) }}
            </span>
        </template>
        <template v-slot:commission>
            <span
                v-b-tooltip.hover.top.d50
                title="Commission"
            >
                {{ cost.commission.fixed ? formatCurrency(cost.commission.value) : formatPercentage(cost.commission.value) }}
            </span>
        </template>
        <template v-slot:commissionDue>
            <span
                v-b-tooltip.hover.top.d50
                title="Commission Due"
            >
                {{ formatCurrency(cost.commission.due) }}
            </span>
        </template>
        <template v-slot:totalProfit>
            <span
                v-b-tooltip.hover.top.d50
                title="Total Profit"
            >
                {{ formatCurrency(cost.total_profit) }}
            </span>
        </template>
        <template v-slot:icons>
            <div>
                <i
                    v-if="cost.notes"
                    v-b-tooltip.html.hover.top.d50
                    :title="cost.notes"
                    class="fa fa-file-text"
                />
                <i
                    v-if="isEditable && !cost.is_approved"
                    v-b-tooltip.hover.top.d50
                    class="fa fa-cube resources-cube-icon"
                    :class="{ 'd-inline-block': hasResources }"
                    :title="resourcesTooltipText"
                    @click="openResourcePickerModal"
                />
                <i
                    v-else
                    class="fa fa-cube"
                    :class="{ 'd-inline-block': hasResources }"
                />
            </div>
        </template>
        <template v-slot:user>
            <EditableUserSelect
                v-if="isEditable && !cost.is_approved"
                :tooltip="`Click to ${cost.user ? 'change' : 'assign'} user...`"
                :value="cost.user"
                @save="patchCost($event, 'user_id')"
            >
                <div v-if="cost.user">
                    {{ cost.user.name }}
                </div>
                <div v-else>
                    <i class="fa fa-user-plus text-muted" />
                </div>
            </EditableUserSelect>
            <div v-else>
                <div v-if="cost.user">
                    {{ cost.user.name }}
                </div>
            </div>
        </template>
        <template v-slot:status>
            <EditableStatusSelect
                v-if="isEditable"
                tooltip="Click to change status..."
                :value="cost.status"
                @save="patchCost($event, 'cost_status_id')"
            >
                <div v-if="cost.status">
                    <CostableTableStatusBadge :status="cost.status" />
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableStatusSelect>
            <div v-else>
                <div v-if="cost.status">
                    <CostableTableStatusBadge :status="cost.status" />
                </div>
            </div>
        </template>
        <template v-slot:secondaryStatus>
            <EditableStatusSelect
                v-if="isEditable"
                tooltip="Click to change secondary status..."
                type="secondary"
                :value="cost.secondary_status"
                @save="patchCost($event, 'secondary_status_id')"
            >
                <div v-if="cost.secondary_status">
                    <CostableTableStatusBadge :status="cost.secondary_status" />
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableStatusSelect>
            <div v-else>
                <div v-if="cost.secondary_status">
                    <CostableTableStatusBadge :status="cost.secondary_status" />
                </div>
            </div>
        </template>
        <template v-slot:transactionType>
            <EditableTransactionTypeSelect
                v-if="isEditable && !cost.is_approved"
                tooltip="Click to change transaction type..."
                :value="cost.transaction_type"
                @save="patchCost($event, 'transaction_type_id')"
            >
                <div v-if="cost.transaction_type">
                    {{ cost.transaction_type.label }}
                </div>
                <div v-else>
                    <i class="fa fa-plus text-muted" />
                </div>
            </EditableTransactionTypeSelect>
            <div v-else>
                <div v-if="cost.transaction_type">
                    {{ cost.transaction_type.label }}
                </div>
            </div>
        </template>
        <template v-slot:reconciled>
            <i
                v-if="cost.reconciled"
                v-b-tooltip.hover.top.d50
                title="Reconciled"
                class="fa fa-check"
            />
        </template>
        <template v-slot:actions>
            <CostableTableCostActionsDropdown
                v-if="isEditable"
                :cost="cost"
            />
        </template>

    </CostableTableRow>
</template>

<script>
import EditableTypeSelect from '@/components/EditableTypeSelect';
import EditableTaxRateSelect from '@/components/EditableTaxRateSelect';
import EditableTextarea from '@/components/EditableTextarea';
import EditableAccountSelect from '@/components/EditableAccountSelect';
import EditableInput from '@/components/EditableInput';
import { formatFloat, formatCurrency, formatPercentage } from '@/utils/helpers';
import costTemplate from '@/models/cost';
import EditableUserSelect from '@/components/EditableUserSelect';
import EditableStatusSelect from '@/components/EditableStatusSelect';
import EditableTransactionTypeSelect from '@/components/EditableTransactionTypeSelect';
import EditableTabSelect from '@/components/EditableTabSelect';
import EditableNominalCodeSelect from '@/components/EditableNominalCodeSelect';
import CostableTableRow from '@/modules/costable/_components/CostableTableRow';
import CostableTableCostActionsDropdown from './CostableTableCostActionsDropdown';
import CostableTableSelectCheckbox from './CostableTableSelectCheckbox';
import CostableTableStatusBadge from './CostableTableStatusBadge';

export default {

    components: {
        CostableTableRow,
        EditableTransactionTypeSelect,
        EditableStatusSelect,
        EditableUserSelect,
        EditableTabSelect,
        CostableTableSelectCheckbox,
        CostableTableCostActionsDropdown,
        EditableTypeSelect,
        EditableTextarea,
        EditableAccountSelect,
        EditableInput,
        EditableTaxRateSelect,
        CostableTableStatusBadge,
        EditableNominalCodeSelect,
    },

    props: {
        cost: {
            type: Object,
            default: () => ({
                ...costTemplate,
                original_quantity: 1,
                total_cost: 0,
                total_price: 0,
                markup: 0,
                margin: 0,
            }),
        },
        groupId: {
            type: Number,
            default: null,
        },
    },

    computed: {
        isEditable() {
            // interface method
            return this.cost.is_editable && this.$store.getters['costable/isEditable'];
        },
        showNominalCodeColumn() {
            return this.$store.getters['costable/showNominalCodeColumn'];
        },
        isApproved() {
            return this.cost.is_approved;
        },
        resourceCount() {
            return this.cost.resources.length;
        },
        hasResources() {
            return this.resourceCount > 0;
        },
        resourcesTooltipText() {
            if (this.hasResources) {
                return `Click to edit ${this.resourceCount} resources...`;
            }
            return 'Click to add resources...';
        },
        isDurationEnabled() {
            return this.$store.getters['costable/isDurationEnabled'];
        },
        isPriceUnitEnabled() {
            return this.$store.getters['costable/isPriceUnitEnabled'];
        },
        hasCommission() {
            return this.$store.getters['costable/hasCommission'];
        },
    },

    methods: {
        formatFloat,
        formatCurrency,
        formatPercentage,
        selectCost(value) {
            this.$store.dispatch('costable/selectCost', {
                groupId: this.cost.group_id,
                costId: this.cost.id,
                value,
            });
        },
        async patchDuration({ value, done }) {
            if (this.isNewCost) {
                await this.$store.dispatch('costable/storeCostWithoutBuildingCost', {
                    group_id: this.groupId,
                    duration: value,
                    display_duration: true,
                });
            } else {
                await this.$store.dispatch('costable/patchCosts', {
                    costIds: [this.cost.id],
                    data: {
                        duration: value,
                        display_duration: true,
                    },
                });
            }
            done();
        },
        async patchCost({ value, isDirty, done }, attribute) {
            if (this.isNewCost) {
                await this.$store.dispatch('costable/storeCostWithoutBuildingCost', {
                    group_id: this.groupId,
                    [attribute]: value,
                });
            } else if (isDirty) {
                await this.$store.dispatch('costable/patchCosts', {
                    costIds: [this.cost.id],
                    data: {
                        [attribute]: value,
                    },
                });
            }
            done();
        },
        formatTime(time) {
            const parts = time.split(':');
            return `${parts[0]}:${parts[1]}`;
        },
        openResourcePickerModal() {
            this.$eventBus.$emit(this.$events.openCostResourcePickerModal, {
                costId: this.cost.id,
            });
        },
    },

};
</script>

<style lang="scss" scoped>
.max-width-100 {
    max-width: 100px;
}
.CostableTableCostHandle {
    cursor: ns-resize;
}
@media (max-width: 768px) {
    .right-on-mobile {
        position: absolute;
        right: 0;
    }
}
.resources-cube-icon {
    display: none;
    cursor: pointer;
}
.CostableTableCost:hover {
    .resources-cube-icon {
        display: inline-block;
    }
}
</style>
